import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore, useSocketStore } from './store';

// Auth
const LoginPage          = React.lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage       = React.lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage  = React.lazy(() => import('./pages/auth/ResetPasswordPage'));

// Dashboard
const DashboardLayout   = React.lazy(() => import('./pages/dashboard/DashboardLayout'));
const OverviewPage      = React.lazy(() => import('./pages/dashboard/OverviewPage'));
const TicketsPage       = React.lazy(() => import('./pages/dashboard/TicketsPage'));
const TicketDetailPage  = React.lazy(() => import('./pages/dashboard/TicketDetailPage'));
const AIAgentsPage      = React.lazy(() => import('./pages/dashboard/AIAgentsPage'));
const AIAgentDetailPage = React.lazy(() => import('./pages/dashboard/AIAgentDetailPage'));
const AgentsPage        = React.lazy(() => import('./pages/dashboard/AgentsPage'));
const CustomersPage     = React.lazy(() => import('./pages/dashboard/CustomersPage'));
const TeamsPage         = React.lazy(() => import('./pages/dashboard/TeamsPage'));
const AnalyticsPage     = React.lazy(() => import('./pages/dashboard/AnalyticsPage'));
const SettingsPage      = React.lazy(() => import('./pages/dashboard/SettingsPage'));
const SLAPage           = React.lazy(() => import('./pages/dashboard/SLAPage'));
const AutomationsPage   = React.lazy(() => import('./pages/dashboard/AutomationsPage'));

// Customer Portal
const PortalLayout      = React.lazy(() => import('./pages/portal/PortalLayout'));
const PortalHome        = React.lazy(() => import('./pages/portal/PortalHome'));
const PortalTickets     = React.lazy(() => import('./pages/portal/PortalTickets'));
const PortalTicketDetail = React.lazy(() => import('./pages/portal/PortalTicketDetail'));
const PortalNewTicket   = React.lazy(() => import('./pages/portal/PortalNewTicket'));
const PortalChat        = React.lazy(() => import('./pages/portal/PortalChat'));

// Super Admin
const SuperAdminLayout  = React.lazy(() => import('./pages/super/SuperAdminLayout'));
const SuperTenantsPage  = React.lazy(() => import('./pages/super/SuperTenantsPage'));
const SuperAnalyticsPage = React.lazy(() => import('./pages/super/SuperAnalyticsPage'));

// Widget
const WidgetPage        = React.lazy(() => import('./pages/widget/WidgetPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries:   { retry: 1, staleTime: 30000, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

const DASHBOARD_ROLES = ['super_admin', 'tenant_admin', 'agent', 'senior_agent', 'team_lead'];

function AuthGuard({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/auth/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { accessToken } = useAuthStore();
  const { connect }     = useSocketStore();
  const { user }        = useAuthStore();

  useEffect(() => { if (accessToken) connect(accessToken); }, [accessToken, connect]);

  return (
    <Routes>
      {/* Auth */}
      <Route path="/auth/login"           element={<LoginPage />} />
      <Route path="/auth/register"        element={<RegisterPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password"  element={<ResetPasswordPage />} />

      {/* Widget */}
      <Route path="/widget" element={<WidgetPage />} />

      {/* Customer Portal */}
      <Route path="/portal" element={<AuthGuard roles={['customer']}><PortalLayout /></AuthGuard>}>
        <Route index           element={<PortalHome />} />
        <Route path="tickets"     element={<PortalTickets />} />
        <Route path="tickets/new" element={<PortalNewTicket />} />
        <Route path="tickets/:id" element={<PortalTicketDetail />} />
        <Route path="chat"        element={<PortalChat />} />
      </Route>

      {/* Super Admin standalone */}
      <Route path="/super" element={<AuthGuard roles={['super_admin']}><SuperAdminLayout /></AuthGuard>}>
        <Route index           element={<Navigate to="/super/tenants" replace />} />
        <Route path="tenants"  element={<SuperTenantsPage />} />
        <Route path="analytics" element={<SuperAnalyticsPage />} />
      </Route>

      {/* Agent / Admin / Super Admin Dashboard */}
      <Route path="/dashboard" element={<AuthGuard roles={DASHBOARD_ROLES}><DashboardLayout /></AuthGuard>}>
        <Route index                element={<OverviewPage />} />
        <Route path="tickets"       element={<TicketsPage />} />
        <Route path="tickets/:id"   element={<TicketDetailPage />} />
        <Route path="ai-agents"     element={<AIAgentsPage />} />
        <Route path="ai-agents/:id" element={<AIAgentDetailPage />} />
        <Route path="agents"        element={<AgentsPage />} />
        <Route path="customers"     element={<CustomersPage />} />
        <Route path="teams"         element={<TeamsPage />} />
        <Route path="analytics"     element={<AnalyticsPage />} />
        <Route path="settings"      element={<SettingsPage />} />
        <Route path="sla"           element={<SLAPage />} />
        <Route path="automations"   element={<AutomationsPage />} />
      </Route>

      {/* Root redirect */}
      <Route path="/" element={
        !user ? <Navigate to="/auth/login" replace /> :
        user.role === 'customer' ? <Navigate to="/portal" replace /> :
        <Navigate to="/dashboard" replace />
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <React.Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading...</p>
            </div>
          </div>
        }>
          <AppRoutes />
        </React.Suspense>
        <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
